import { observable, decorate, computed } from 'mobx';
import _ from 'lodash';
import { POSTypesTitles, timeZonesTitles } from '../dictionary';
import { authUserState, uistate } from './common';
import { checkRights, collectCheckBox } from '../functions';
import { WorkDays } from '../shared/WorkDaysModel';

/** ----- КЛАСС РАБОТЫ С НАСТРОЙКАМИ ФИЛИАЛОВ ----- */
export class FilialState {
	constructor() {
		this.Filials = [];
		this.IsSendRequest = false;
		this.Filial = undefined;
		this.User = undefined;

		this.Cities = [];
		this.PaymentMethods = [];
		this.Brands = [];
		this.Aggregators = [];
		this.Entities = [];
		this.CassaList = [];

	}

	get CanManage () { return authUserState.User ? checkRights("ManageFilials") : false; }

	/**
	 * Разбор ответа от сервера со списком филиалов
	 * @param {object} data 
	 */
	setFilials (data) {
		const canRemove = data.Success && data.Filials.length > 1;

		this.Filials = data.Success ? _.map(data.Filials, (filial) => {
			const types = _.chain(filial.POS || [])
				.flatMap((pos) => pos.Types || [])
				.map("Type")
				.uniq()
				.map((type) => _.find(POSTypesTitles, { Id : type }))
				.compact()
				.map("ShortName")
				.value();

			return {
				...filial,
				POSTypes: types.join(", "),
				CanRemove: canRemove
			};
		}) : [];
		this.IsSendRequest = true;
	}

	/** Формирование столбцов для таблицы */
	get columns() {
		return {
			Name: {},
			City: { width: 100 },
			...(!uistate.IsMobile ? {
				Address: {},
				Phone: { width: 120 }
			} : {}),
			...(authUserState.HasERP ? {
				POSTypes: { filterable: false }
			} : {}),
			...(!uistate.IsMobile ? {
				WorkDays: { filterable: false }
			} : {}),
			...(this.CanManage ? {
				Edit: {},
				Remove: {}
			} : {})
		};
	}

	/**
	 * Разбор ответа от сервера с данными филиала
	 * @param {object} data 
	 */
	setFilial(data) { this.Filial = _.isUndefined(data) ? undefined : new Filial(data, this.Cities); }
}
decorate(FilialState, {
	Filials: observable,
	IsSendRequest: observable,
	Filial: observable,
	User: observable,

	Cities: observable,
	PaymentMethods: observable,
	Brands: observable,
	Aggregators: observable,
	Entities: observable,
	CassaList: observable,

	CanManage: computed
});

/** ----- КЛАСС ФИЛИАЛА ----- */
export class Filial {
	constructor(item, cities = []) {
		this.Cities = cities;

		this.Id = item && item.Id ? item.Id : -1;
		this.Name = item && item.Name ? item.Name : "";
		this.CityId = item && item.CityId ? item.CityId : -1;
		this.Active = item && !_.isUndefined(item.Active) ? item.Active : true;
		this.Address = item && item.Address ? item.Address : "";
		this.Phone = item && item.Phone ? item.Phone : "";
		this.TimeZone = item && item.GMT ? item.GMT : "";
		this.ShiftHour = item && item.ShiftHour ? item.ShiftHour : 0;

		this.WorkDays = new WorkDays(item);

		this.Stocks = [];
		this.Kitchen = undefined;
		this.Bar = undefined;
		if (!_.isUndefined(item) && this.isERP)
			if (!_.isEmpty(item.Stocks)) {
				_.each(item.Stocks, (stock) => { this.Stocks.push(new FilialStock(stock)); });
				this.Kitchen = _.find(item.Stocks, { Kitchen : true }) ? _.find(item.Stocks, { Kitchen : true }).Id : undefined;
				this.Bar = _.find(item.Stocks, { Bar : true }) ? _.find(item.Stocks, { Bar : true }).Id : undefined;
			} else this.Stocks.push(new FilialStock());

		this.POS = [];
		this.MainPOS = undefined;
		if (!_.isUndefined(item) && this.isERP)
			if (!_.isEmpty(item.POS)) {
				_.each(item.POS, (pos) => { this.POS.push(new Pos(pos)); });
				this.MainPOS = _.findIndex(item.POS, { Main : true });
			}

		this.ButtonLoading = false;
		this.ErrorCode = "";
		this.SuccessSave = false;
	}

	get isERP () { return !authUserState.isCallCentre && authUserState.HasERP; }
	
	/**
	 * Сбор данных с формы филиала
	 * @param {string} id название параметра
	 * @param {string} value значение параметра
	 */
	collect(id, value) {
		this[id] = value;

		if (id === "CityId") {
			const city = _.find(this.Cities, { Id : value });
			this.TimeZone = city ? city.GMT : "";
		}
	}

	/**
	 * Назначение склада для списания продукции
	 * @param {string} id тип назначения: кухня или бар
	 * @param {boolean} value флаг выбора
	 * @param {number} index порядковый номер склада
	 */
	collectStockType(id, value, index) {
		if (/Kitchen/.test(id)) this.Kitchen = value ? this.Stocks[index].Id : undefined;
		else if (/Bar/.test(id)) this.Bar = value ? this.Stocks[index].Id : undefined;
	}

	/** Получение названия часового пояса */
	get TimeZoneTitle() {
		var city = _.find(this.Cities, { Id : this.CityId }),
			timezone = !_.isUndefined(city) ? timeZonesTitles[city.GMT] : "",
			timezoneTitle = timezone === "" ? "" : ", час. пояс: " + timezone;
		return timezoneTitle;
	}

	/** Добавление склада */
	addStock() { this.Stocks.push(new FilialStock()); }
	/**
	 * Удаление склада
	 * @param {number} index порядковый номер склада
	 */
	removeStock(index) { this.Stocks = _.filter(this.Stocks, (v,i) => { return i !== index; }); }

	/** Добавление точки продаж */
	addPOS() { this.POS.push(new Pos()); }
	/**
	 * Удаление точки продаж
	 * @param {number} index порядковый номер точки продаж
	 */
	removePOS(index) {
		this.POS = _.filter(this.POS, (v,i) => { return i !== index; });

		if (this.MainPOS === index) this.MainPOS = undefined;
		else if (this.MainPOS > index) this.MainPOS--;
	}

	/**
	 * Проверка на корректность заполненных данных по филиалу
	 * 1. Корректность заполнения номера телефона
	 * 2. Если есть склады, должны быть выбраны склады для кухни и для бара
	 * 3. Если есть точки продаж, должна быть указана одна основная
	 * 4. Если есть точки продаж, к точке должно быть привязано юр. лицо
	 * 5. Если есть точки продаж, для каждой точки должен быть выбран хотя бы один тип точки
	 * @returns {boolean} флаг корректности данных
	 */
	validate () {
		var isValid = true;

		this.ErrorCode = "";
		this.SuccessSave = false;

		if (this.CityId === "" || this.CityId < 1) {
			this.ErrorCode = "WRONG_PARAMS_CITY";
			return false;
		}

		if (this.Phone !== "" && this.Phone.replace(/\D/gi,"").length < 11) {
			this.ErrorCode = "WRONG_PHONE_LENGTH";
			return false;
		}

		if (authUserState.HasERP) {
			if (!_.isEmpty(this.Stocks) && (
				this.Kitchen === undefined ||
				this.Bar === undefined ||
				!_.some(this.Stocks, { Id : this.Kitchen }) ||
				!_.some(this.Stocks, { Id : this.Bar })
			)) {
				this.ErrorCode = "FILIAL_MENU_REQUIRED";
				return false;
			}
	
			if (!_.isEmpty(this.POS) && (
				this.MainPOS === undefined ||
				this.MainPOS < 0 ||
				this.MainPOS >= this.POS.length
			)) {
				this.ErrorCode = "FILIAL_MAIN_REQUIRED";
				return false;
			}
	
			if (!_.isEmpty(this.POS)) 
				_.each(this.POS, (v) => {
					if (v.EntityId === "" || v.EntityId === -1) {
						this.ErrorCode = "FILIAL_ENTITY_REQUIRED";
						isValid = false;
						return false;
					}
	
					if (!_.some(_.map(v.Types, "Active"))) {
						this.ErrorCode = "POSTYPE_REQUIRED";
						isValid = false;
						return false;
					}
				});
		}

		return isValid;
	}

	getData () {
		var data = {
			Id : this.Id,
			Name : this.Name,
			CityId : this.CityId,
			Active : this.Active,
			Address : this.Address,
			Phone : this.Phone.replace(/\D/gi,""),
			TimeZone : this.TimeZone,
			ShiftHour : this.ShiftHour
		}

		_.each(this.WorkDays.getData(), (day, i) => {
			const name = "WorkDays." + i + ".";
			_.each(day, (value, field) => { data[name + field] = value; });
		});
		_.each(this.Stocks, (stock, i) => {
			const stockData = stock.getData();
			const name = "Stocks." + i + ".";

			if (stockData.Name !== "") {
				data[name + "Id"] = stockData.Id;
				data[name + "Name"] = stockData.Name;
				data[name + "Kitchen"] = stockData.Id === this.Kitchen;
				data[name + "Bar"] = stockData.Id === this.Bar;
			}
		});
		_.each(this.POS, (pos, i) => {
			const posData = pos.getData();
			const name = "POS." + i + ".";

			if (posData.Name !== "") {
				data[name + "Id"] = posData.Id;
				data[name + "Name"] = posData.Name;
				data[name + "HasCassa"] = posData.HasCassa;
				data[name + "Main"] = i === this.MainPOS;
				data[name + "Brands"] = posData.Brands.join(",");
				data[name + "Aggregators"] = posData.Aggregators.join(",");
				data[name + "PaymentMethods"] = posData.PaymentMethods.join(",");
				data[name + "EntityId"] = posData.EntityId;

				if (posData.HasCassa) {
					data[name + "Fiscal"] = posData.Fiscal;
					data[name + "CassaType"] = posData.CassaType;
					data[name + "FiscalCassaName"] = posData.FiscalCassaName;
				}

				_.each(posData.Types, (type, typeIndex) => {
					const typeName = name + "Types." + typeIndex + ".";
					_.each(type, (value, field) => { data[typeName + field] = value; });
				});
			}
		});

		return data;
	}
}
decorate(Filial, {
	Name: observable,
	CityId: observable,
	Address: observable,
	Phone: observable,
	TimeZone: observable,
	ShiftHour: observable,
	Kitchen: observable,
	Bar: observable,
	MainPOS: observable,

	WorkDays: observable,
	Stocks: observable,
	POS: observable,

	ButtonLoading: observable,	
	ErrorCode: observable,
	SuccessSave: observable
});

/** ----- КЛАСС СКЛАДА ----- */
export class FilialStock {
	constructor(item) {
		this.Id = item && item.Id ? item.Id : -1;
		this.Name = item && item.Name ? item.Name : "";
	}

	/**
	 * Сбор данных склада с формы
	 * @param {string} id название параметра
	 * @param {string} value значение параметра
	 */
	collect(id, value) { this[id] = value; }

	/**
	 * Формирование данных склада
	 * @returns {object} данные склада
	 */
	getData() {
		return {
			Id: this.Id,
			Name: this.Name
		};
	}
}
decorate(FilialStock, {
	Name: observable,
})

/** ----- КЛАСС ТОЧКИ ПРОДАЖ ----- */
export class Pos {
	constructor(item) {
		this.Id = item && item.Id ? item.Id : -1;
		this.Name = item && item.Name ? item.Name : "";
		this.HasCassa = item && !_.isUndefined(item.HasCassa) ? item.HasCassa : false;
		this.EntityId = item && !_.isUndefined(item.EntityId) ? item.EntityId : -1;
		this.Brands = item && !_.isUndefined(item.Brands) && !_.isNull(item.Brands) ? item.Brands : [];
		this.Aggregators = item && !_.isUndefined(item.Aggregators) && !_.isNull(item.Aggregators) ? item.Aggregators : [];
		this.PaymentMethods = item && !_.isUndefined(item.PaymentMethods) && !_.isNull(item.PaymentMethods) ? item.PaymentMethods : [];
		this.Main = item && !_.isUndefined(item.Main) ? item.Main : false;
		this.Fiscal = item && !_.isUndefined(item.Fiscal) ? item.Fiscal : false;
		this.CassaType = item && !_.isUndefined(item.CassaType) ? item.CassaType : -1;
		this.FiscalCassaName = item && !_.isUndefined(item.FiscalCassaName) ? item.FiscalCassaName : -1;

		this.Types = [];
		_.each(POSTypesTitles, (type) => { 
			var posType = item && item.Types ? _.find(item.Types, { Type : type.Id }) : undefined;
			this.Types.push(new POSType(_.extend({ Type : type.Id, Name : type.Name }, posType))); 
		});
	}

	/**
	 * Сбор данных точки продаж с формы
	 * @param {string} id название параметра
	 * @param {boolean|number|string} value значение параметра
	 */
	collect(id, value) {
		const fieldId = id.split("_")[1] ? parseInt(id.split("_")[1], 10) : "";

		if (/Brands/.test(id)) this.Brands = collectCheckBox(fieldId, value, this.Brands);
		else if (/Aggregators/.test(id)) this.Aggregators = collectCheckBox(fieldId, value, this.Aggregators);
		else if (/PaymentMethods/.test(id)) this.PaymentMethods = collectCheckBox(fieldId, value, this.PaymentMethods);
		else this[id] = value;
	}

	/**
	 * Формирование данных точки продаж
	 * @returns {object} данные точки продаж
	 */
	getData() {
		const data = {
			Id: this.Id,
			Name: this.Name,
			HasCassa: this.HasCassa,
			Brands: this.Brands,
			Aggregators: this.Aggregators,
			PaymentMethods: this.PaymentMethods,
			EntityId: this.EntityId,
			Types: _.map(_.filter(this.Types, "Active"), (type) => type.getData())
		};

		if (this.HasCassa) {
			data.Fiscal = this.Fiscal;
			data.CassaType = this.CassaType;
			data.FiscalCassaName = this.FiscalCassaName;
		}

		return data;
	}
}
decorate(Pos, {
	Name: observable,
	HasCassa: observable,
	EntityId: observable,
	Brands: observable,
	Aggregators: observable,
	PaymentMethods: observable,
	Main: observable,
	Fiscal: observable,
	CassaType: observable,
	FiscalCassaName: observable,
	Types: observable
})

/** ----- КЛАСС ТИПА ТОЧКИ ПРОДАЖ ----- */
export class POSType {
	constructor(item) {
		this.Id = item && item.Id ? item.Id : -1;
		this.Type = item && item.Type ? item.Type : -1;
		this.Name = item && item.Name ? item.Name : "";
		this.Active = item && item.Id ? true : false;
		this.FirstTime = item && item.FirstTime ? item.FirstTime : 0;
		this.FirstToTime = item && item.FirstToTime ? item.FirstToTime : 0;
		this.LastTime = item && item.LastTime ? item.LastTime : 0;
		this.LastToTime = item && item.LastToTime ? item.LastToTime : 0;
	}

	/**
	 * Сбор данных типа точки продаж с формы
	 * @param {string} id название параметра
	 * @param {boolean|number} value значение параметра
	 */
	collect(id, value) { this[id] = value; }

	/**
	 * Формирование данных типа точки продаж
	 * @returns {object} данные типа точки продаж
	 */
	getData() {
		return {
			Id: this.Id,
			Type: this.Type,
			FirstTime: this.FirstTime,
			FirstToTime: this.FirstToTime,
			LastTime: this.LastTime,
			LastToTime: this.LastToTime
		};
	}
}
decorate(POSType, {
	Type: observable,
	Active: observable,
	FirstTime: observable,
	FirstToTime: observable,
	LastTime: observable,
	LastToTime: observable
})
